import SEO from '../pages/SEO';
import React from "react";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle2 } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

export default function JointReplacementPage() {
  const procedures = [
    {
      title: "Total Knee Replacement",
      description:
        "Complete replacement of damaged knee joint surfaces with prosthetic components",
    },
    {
      title: "Partial Knee Replacement",
      description:
        "Replacement of only the damaged compartment, preserving healthy tissue",
    },
    {
      title: "Total Hip Replacement",
      description:
        "Replacement of hip ball and socket with durable prosthetic implants",
    },
    {
      title: "Shoulder Replacement",
      description:
        "Replacement of damaged shoulder joint for severe arthritis or fractures",
    },
  ];

  const benefits = [
    "Significant pain relief",
    "Improved mobility and function",
    "Better quality of life",
    "Modern, durable implants",
    "Personalized surgical approach",
    "Comprehensive rehabilitation",
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Knee & Hip Replacement Surgeon in Hyderabad"
        description="Explore knee, hip and other joint replacement consultations with Dr. Sujit Kumar in Hyderabad, including personalized assessment and recovery planning."
        keywords="knee replacement surgeon Hyderabad, hip replacement doctor Hyderabad, joint replacement specialist, arthroplasty Hyderabad"
        canonical="https://www.drsujitkumar.com/treatments/joint-replacement"
        robots="index, follow"
      />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/90 text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4 font-[family-name:var(--font-playfair)]">
            Joint Replacement
          </h1>
          <p className="text-lg text-primary-foreground/90 leading-relaxed max-w-2xl mx-auto">
            Advanced joint replacement surgery for lasting pain relief and improved function.
          </p>
        </div>
      </section>

      {/* Main Section */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-start">
            {/* Left Content */}
            <div>
              <h2 className="text-3xl font-bold mb-6 font-[family-name:var(--font-playfair)]">
                When is Joint Replacement Considered?
              </h2>
              <div className="prose prose-lg max-w-none space-y-4">
                <p className="text-foreground leading-relaxed">
                  Joint replacement is typically considered when severe arthritis or joint damage causes
                  persistent pain and limits daily activities despite conservative treatments like medications,
                  injections, and physiotherapy.
                </p>
                <p className="text-foreground leading-relaxed">
                  We offer total and partial replacement for knee, hip, and shoulder using modern implants with
                  proven track records. Our personalized approach includes detailed pre-operative planning,
                  minimally invasive techniques when appropriate, and structured recovery protocols.
                </p>
              </div>

              {/* Benefits List */}
              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Benefits</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-xl">
              <img
                src="/joint-replacement-surgery-knee-hip-orthopedic-pro.jpg"
                alt="Joint replacement surgery"
                className="object-cover w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Procedures Section */}
      <section className="py-16 bg-muted/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 font-[family-name:var(--font-playfair)]">
            Procedures We Offer
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 max-w-4xl mx-auto">
            {procedures.map((procedure, index) => (
              <Card key={index}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-2">{procedure.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {procedure.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
